// SYSTEM HEALTH (redesign stage 1): read-only, from what the app already reports - GET /system-status (the ONE status
// model: Zerodha session, market and feed per exchange, warnings) and the options worker's /health. No new backend.
import React, { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import optionsApi from "@/lib/optionsApi";
import { useApp } from "@/context/AppContext";
import { headerWarnings, marketBadge } from "@/lib/statusModel";
import { visibleInterval } from "@/lib/poll";

const TONE = { good: "text-[#00C805]", idle: "text-[var(--term-secondary)]", warn: "text-[#B45309]", bad: "text-[#FF3B30]" };
const SEV = { critical: "text-[#FF3B30]", warning: "text-[#B45309]", info: "text-[var(--term-secondary)]" };

function Tile({ label, value, tone, sub, testId }) {
  return (
    <div className="term-panel rounded-md border border-[var(--term-border)] px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]">{label}</div>
      <div className={`text-sm font-bold ${TONE[tone] || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--term-muted)] whitespace-pre-line">{sub}</div>}
    </div>
  );
}

export default function SystemHealth() {
  const { systemStatus } = useApp() || {};
  const [worker, setWorker] = useState(undefined);
  useEffect(() => {
    const load = () => optionsApi.get("/health").then((r) => setWorker(r.data)).catch(() => setWorker(null));
    load();
    return visibleInterval(load, 15000);
  }, []);
  const mb = marketBadge(systemStatus);
  const warnings = headerWarnings(systemStatus);
  const z = systemStatus?.zerodha_session;
  const exchanges = Object.entries(systemStatus?.market?.exchanges || {});
  return (
    <div className="p-4 md:p-6 max-w-5xl space-y-4" data-testid="system-health-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><Gauge className="h-5 w-5" />System Health</h1>
      {!systemStatus ? <div className="text-xs text-[var(--term-muted)]">Checking status…</div> : <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Tile label="Zerodha session" value={z || "—"} tone={z === "CONNECTED" ? "good" : "bad"} testId="health-session" />
          <Tile label="Market data" value={mb?.text || "—"} tone={mb?.tone} sub={mb?.label} testId="health-market" />
          <Tile label="Options worker" value={worker === undefined ? "checking…" : worker ? "RUNNING" : "UNREACHABLE"}
            tone={worker ? "good" : worker === null ? "bad" : "idle"} sub={worker ? `read-only ${worker.read_only ? "yes" : "no"}` : null} testId="health-worker" />
        </div>
        {exchanges.length > 0 && <div className="term-panel rounded-md border border-[var(--term-border)] overflow-x-auto">
          <table className="w-full text-xs min-w-[480px]" data-testid="health-exchanges">
            <thead className="text-[10px] uppercase tracking-wide text-[var(--term-muted)]"><tr>
              <th className="text-left px-3 py-2">Exchange</th><th className="text-left">Session</th><th className="text-left">Hours</th><th className="text-left">Last trade</th></tr></thead>
            <tbody>{exchanges.map(([ex, v]) => <tr key={ex} className="border-t border-[var(--term-border)]">
              <td className="px-3 py-2 font-bold">{ex}</td>
              <td className={v.open ? "text-[#00C805] font-bold" : "text-[var(--term-secondary)]"}>{v.open ? "OPEN" : `closed${v.reason ? ` · ${String(v.reason).replace(/_/g, " ").toLowerCase()}` : ""}`}</td>
              <td className="mono">{v.opens ? `${v.opens}–${v.closes}` : "—"}</td><td className="mono">{v.last_trade_label || "—"}</td></tr>)}</tbody>
          </table></div>}
        <section className="term-panel rounded-md border border-[var(--term-border)] p-3" data-testid="health-warnings">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)] mb-1.5">Warnings</h2>
          {warnings.length === 0 ? <div className="text-xs text-[#00C805]">No warnings.</div>
            : <ul className="space-y-1 text-xs">{warnings.map((w, i) => <li key={i} className={SEV[w.severity] || ""}><b>{(w.severity || "").toUpperCase()}</b> · {w.text || w.message || w.code}</li>)}</ul>}
        </section>
      </>}
      <div className="text-[11px] text-[var(--term-muted)]">Read-only. Trading health per trade (reconciliation, recovery) is on each trade; admin readiness is under Broker Session.</div>
    </div>
  );
}
